<!--
  AdminTokenExport.svelte — Exports the token ledger for import into Polyverse.

  Polyverse's token ledger moved in-house, so the balances people accumulated here have to reach it.
  This is deliberately a file an operator downloads and uploads rather than a connection between the
  two databases: that coupling would need to exist for exactly one migration and would then sit in
  the configuration forever, with production credentials for another system attached to it.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { Alert, Button, Checkbox, Heading, Label, Spinner } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { AbstractDatepicker } from '../../components';
	import { downloadTokenExport, getTokenExportSummary, type TokenExportSummary } from '../../AppNetwork';
	import { toastError, toastSuccess } from '../../toast';
	import { t, t2 } from '../../i18n';

	/** What an export would contain, fetched before one is built. */
	let summary: TokenExportSummary | null = $state(null);
	/** Whether the summary is still loading. */
	let loading = $state(true);
	/** Error banner text. */
	let error = $state('');

	/** Whether to include the ledger behind each balance, or only the balances. */
	let includeTransactions = $state(true);
	/** Optional floor on the ledger. Balances are always complete regardless. */
	let since = $state('');

	/** Whether a download is in flight. */
	let exporting = $state(false);
	/** What the last successful export produced, so the operator can see it worked. */
	let lastExport: { filename: string; sizeInBytes: number } | null = $state(null);

	onMount(async () => {
		/* A failed summary must not leave the page spinning: the export itself still works without
		   it, so `loading` is cleared in `finally` and the error shown alongside the controls. */
		try {
			summary = await getTokenExportSummary();
		} catch (e) {
			error = e instanceof Error ? e.message : String(e);
		} finally {
			loading = false;
		}
	});

	/**
	 * Renders a byte count at human scale.
	 *
	 * @param bytes The size.
	 */
	function fnFormatSize(bytes: number): string {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	}

	/** Renders a count with thousands separators, since these numbers get large. */
	function fnFormatCount(value: number): string {
		return new Intl.NumberFormat().format(value);
	}

	/** Builds the file and hands it to the browser. */
	async function fnExport(): Promise<void> {
		exporting = true;
		error = '';

		/* The banner and the toast both carry the failure: the toast is what an operator notices, and
		   the banner is what is still on screen when they come back to read it. */
		try {
			lastExport = await downloadTokenExport({
				includeTransactions,
				since: since.length > 0 ? since : undefined
			});
			toastSuccess(t2('Exported {{filename}}.', { filename: lastExport.filename }));
		} catch (e) {
			const message = e instanceof Error ? e.message : String(e);
			error = message;
			toastError(t2('Export failed: {{error}}', { error: message }));
		} finally {
			exporting = false;
		}
	}
</script>

<Heading tag="h2" class="mb-2">{$t('Token Export')}</Heading>
<p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
	{$t('Exports every token balance as a JSON file, for import into Polyverse. Nothing here is modified — an export can be taken as often as needed.')}
</p>

{#if loading}
	<Spinner size="6" />
{:else}
	{#if error}
		<Alert color="red" class="mb-4">{error}</Alert>
	{/if}

	{#if summary}
		<AbstractCard class="mb-4">
			<Heading tag="h3" class="mb-4">{$t('What will be exported')}</Heading>
			<div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
				<div class="text-center">
					<p class="text-sm text-gray-500 dark:text-gray-400">{$t('Accounts with tokens')}</p>
					<Heading tag="h4" class="!mb-0">{fnFormatCount(summary.userCount)}</Heading>
				</div>
				<div class="text-center">
					<p class="text-sm text-gray-500 dark:text-gray-400">{$t('Ledger entries')}</p>
					<Heading tag="h4" class="!mb-0">{fnFormatCount(summary.transactionCount)}</Heading>
				</div>
				<div class="text-center">
					<p class="text-sm text-gray-500 dark:text-gray-400">{$t('Total balance')}</p>
					<Heading tag="h4" class="!mb-0">{fnFormatCount(summary.totalBalance)}</Heading>
				</div>
			</div>
			<p class="text-xs text-gray-500 dark:text-gray-400 mt-4">
				{$t('Check the total balance against what Polyverse reports after the import. The two should agree exactly.')}
			</p>
		</AbstractCard>
	{/if}

	<AbstractCard class="mb-4">
		<Heading tag="h3" class="mb-4">{$t('Options')}</Heading>

		<Checkbox bind:checked={includeTransactions} class="mb-2">
			{$t('Include the full ledger')}
		</Checkbox>
		<p class="text-xs text-gray-500 dark:text-gray-400 mb-4 ml-6">
			{$t("Balances alone are enough to carry the money across. Including the ledger is what makes each customer's history survive the move, at the cost of a much larger file.")}
		</p>

		{#if includeTransactions}
			<Label class="space-y-2 mb-2">
				<span>{$t('Only entries from')}</span>
				<div class="w-44">
					<!-- `stringValue`, not `value`: the component's `value` is a `Date`, and what
					     travels to the server is the ISO string. -->
					<AbstractDatepicker mode="date" bind:stringValue={since} showRelativeDate={false} />
				</div>
			</Label>
			<p class="text-xs text-gray-500 dark:text-gray-400 mb-4">
				{$t('Optional, and only trims the ledger. Balances are always exported in full, so a shortened history can never be mistaken for a smaller balance.')}
			</p>
		{/if}

		<Button color="primary" disabled={exporting} onclick={fnExport}>
			{#if exporting}
				<Spinner size="4" class="mr-2" />
				{$t('Building the file…')}
			{:else}
				<i class="fa-solid fa-download mr-2"></i>
				{$t('Download Export')}
			{/if}
		</Button>

		{#if exporting && includeTransactions && (summary?.transactionCount ?? 0) > 100000}
			<p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
				{$t('A full ledger of this size takes a while to build. Leave this page open.')}
			</p>
		{/if}

		{#if lastExport}
			<Alert color="green" class="mt-4">
				{$t('Saved {{filename}} ({{size}}).', {
					filename: lastExport.filename,
					size: fnFormatSize(lastExport.sizeInBytes)
				})}
			</Alert>
		{/if}
	</AbstractCard>

	<AbstractCard>
		<Heading tag="h3" class="mb-2">{$t('Next step')}</Heading>
		<p class="text-sm text-gray-500 dark:text-gray-400">
			{$t('Upload the downloaded file in Polyverse under Settings → Admin → Token Import. Import there first as a dry run: it reports what it would do without writing anything, including any account it could not match.')}
		</p>
	</AbstractCard>
{/if}
